// Custom hook for managing resizable panels with drag functionality
import { useState, useCallback, useEffect, useRef } from 'react';

const useResizablePanels = (initialSizes = [25, 25, 50]) => {
    // Load saved sizes from localStorage or use defaults
    const [sizes, setSizes] = useState(() => {
        const saved = localStorage.getItem('workflow-panel-sizes');
        return saved ? JSON.parse(saved) : initialSizes;
    });

    const [isDragging, setIsDragging] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const containerRef = useRef(null);
    const startPos = useRef(0);
    const startSizes = useRef([]);
    
    // Use refs for dragging state to avoid stale closures
    const isDraggingRef = useRef(false);
    const dragIndexRef = useRef(null);

    // Save sizes to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('workflow-panel-sizes', JSON.stringify(sizes));
    }, [sizes]);

    const handleMouseMove = useCallback((e) => {
        if (!isDraggingRef.current || dragIndexRef.current === null || !containerRef.current) {
            return;
        }
        
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const deltaX = e.clientX - startPos.current;
        const deltaPercent = (deltaX / containerWidth) * 100;

        const newSizes = [...startSizes.current];
        const leftIndex = dragIndexRef.current;
        const rightIndex = leftIndex + 1;
        
        if (rightIndex < newSizes.length) {
            const newLeftSize = Math.max(15, Math.min(70, newSizes[leftIndex] + deltaPercent));
            const newRightSize = Math.max(15, Math.min(70, newSizes[rightIndex] - deltaPercent));
            
            // Only update if both panels are within valid bounds
            if (newLeftSize >= 15 && newRightSize >= 15 && newLeftSize <= 70 && newRightSize <= 70) {
                newSizes[leftIndex] = newLeftSize;
                newSizes[rightIndex] = newRightSize;
                setSizes(newSizes);
            }
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        isDraggingRef.current = false;
        dragIndexRef.current = null;
        setIsDragging(false);
        setDragIndex(null);
        
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    const handleMouseDown = useCallback((index, e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Update both state and refs
        setIsDragging(true);
        setDragIndex(index);
        isDraggingRef.current = true;
        dragIndexRef.current = index;
        
        startPos.current = e.clientX;
        startSizes.current = [...sizes];
        
        // Add global mouse move/up listeners
        document.addEventListener('mousemove', handleMouseMove, { passive: false });
        document.addEventListener('mouseup', handleMouseUp, { passive: false });
    }, [sizes, handleMouseMove, handleMouseUp]);

    // Reset to default sizes
    const resetSizes = useCallback(() => {
        setSizes(initialSizes);
    }, [initialSizes]);

    // Cleanup listeners on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    return {
        sizes,
        isDragging,
        dragIndex,
        containerRef,
        handleMouseDown,
        resetSizes
    };
};

export default useResizablePanels;